/**
 * Concat Video Exporter
 *
 * STRATÉGIE OPTIMISÉE en 2 étapes pour éviter les crashs mémoire sur vidéos longues (1h30+) :
 *
 * ÉTAPE 1 : CRÉER LA BASE VIDÉO (20-70%)
 * - Coller les clips VIDÉO normaux (sans clipRole) bout à bout
 * - Utiliser ffmpeg concat avec -c copy (ultra rapide, pas de réencodage)
 * - Résultat : base.mp4 avec tous les clips collés + audio
 *
 * ÉTAPE 2 : AJOUTER LES OVERLAYS (70-100%)
 * - Charger UNIQUEMENT 3 vidéos max en mémoire :
 *   * base.mp4 (vidéo collée)
 *   * intro source (si présente, VP9 avec alpha)
 *   * outro source (si présente, VP9 avec alpha)
 * - Rendre frame par frame : base + intro/outro/text/images superposés
 * - Encoder avec WebCodecs VP9 alpha pour préserver transparence
 * - Multiplexer audio de la base avec ffmpeg
 *
 * AVANTAGES :
 * - Ultra rapide : concat ffmpeg -c copy pour la base (pas de réencodage)
 * - Pas de crash mémoire : 3 vidéos max chargées (au lieu de dizaines)
 * - Simple : pas de génération séparée intro/outro, on charge directement les sources
 * - Transparence préservée : intro/outro sources VP9 avec alpha channel
 * - Audio complet : multiplexé depuis la base
 * - Qualité préservée : aucune perte sur la base
 */

import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';
import { Clip, Track, SubtitleStyle } from './types';
import { WebCodecsExporter, ExportConfig, ProgressCallback, ExportProgress, ASPECT_RATIOS } from './webcodecs-exporter';
import { FrameRenderer } from './frame-renderer';
import { WebMWriter } from './webm-writer';
import { FFmpegOutOfMemoryError, isLikelyFFmpegOutOfMemoryError } from './ffmpeg-errors';
import { FFmpegService } from './ffmpeg-service';
import { getClipExportSourceUrl } from './utils/clip-helpers';

export type ConcatExportConfig = ExportConfig;

interface TimelineSegment {
  type: 'video' | 'black';  // 'video' = clip vidéo, 'black' = zone noire
  clip?: Clip;              // Le clip si type='video'
  start: number;            // Position sur timeline
  duration: number;         // Durée
}

interface TimelineAnalysis {
  segments: TimelineSegment[];  // Tous les segments (vidéo + zones noires)
  overlayClips: Clip[];         // intro/outro/text/images à ajouter par-dessus
  totalDuration: number;        // Durée totale de la timeline
}

export class ConcatExporter {
  private config: ConcatExportConfig;
  private progressCallback: ProgressCallback;
  private ffmpeg: FFmpeg | null = null;
  private isFFmpegLoaded = false;
  private aborted = false;
  private webCodecsExporter: WebCodecsExporter;
  private sourceCache = new Map<string, string>(); // url -> ffmpeg filename
  private sourceSequence = 0;
  private ffmpegLogs: string[] = [];

  constructor(config: ConcatExportConfig, progressCallback: ProgressCallback) {
    this.config = config;
    this.progressCallback = progressCallback;
    this.webCodecsExporter = new WebCodecsExporter(config, progressCallback);
  }

  /**
   * Abort export
   */
  abort(): void {
    this.aborted = true;
    this.webCodecsExporter.abort();
  }

  /**
   * Main export function
   */
  async export(
    clips: Clip[],
    tracks: Track[],
    activeSubtitleLanguage: string,
    subtitleStyle: SubtitleStyle
  ): Promise<Blob> {
    try {
      this.updateProgress({ phase: 'preparing', progress: 0, message: 'Analyse du timeline...' });

      // Phase 1: Analyser la timeline (0-10%)
      const analysis = this.analyzeTimeline(clips, tracks);

      // Fallback : si pas de segments vidéo, utiliser WebCodecs
      if (analysis.segments.filter(s => s.type === 'video').length === 0) {
        this.updateProgress({
          phase: 'preparing',
          progress: 5,
          message: 'Pas de clips vidéo détectés, utilisation de WebCodecs...'
        });
        return this.webCodecsExporter.export(clips, tracks, activeSubtitleLanguage, subtitleStyle);
      }

      const videoCount = analysis.segments.length; // Tous les segments sont des vidéos maintenant

      this.updateProgress({
        phase: 'preparing',
        progress: 10,
        message: `${videoCount} clips vidéo, ${analysis.overlayClips.length} overlays (text/images)`
      });

      // Phase 2: Charger ffmpeg (10-15%)
      await this.loadFFmpeg();
      this.updateProgress({ phase: 'preparing', progress: 15, message: 'FFmpeg chargé' });

      // Phase 4: Ajouter overlays et/ou sous-titres si nécessaire (70-100%)
      const hasOverlays = analysis.overlayClips.length > 0;
      const hasSubtitles = this.checkForSubtitles(clips, activeSubtitleLanguage);
      let baseVideoBlob: Blob | null = null;

      // Si PAS d'overlays, on doit créer la base vidéo (chemin classique rapide)
      if (!hasOverlays) {
        // Phase 3: Créer la base vidéo (15-70%)
        baseVideoBlob = await this.createBaseVideo(analysis);
        this.updateProgress({ phase: 'muxing', progress: 70, message: 'Base vidéo créée' });
      }

      if (hasOverlays || hasSubtitles) {
        // Cas 1: SEULEMENT des sous-titres (pas d'overlays) => Utiliser FFmpeg (ultra rapide)
        if (hasSubtitles && !hasOverlays && baseVideoBlob) {
          this.updateProgress({
            phase: 'muxing',
            progress: 75,
            message: 'Ajout des sous-titres avec FFmpeg (rapide)...'
          });

          // ... (code sous-titres inchangé) ...\n          // Collecter tous les sous-titres des clips vidéo utilisés dans la base
          // CRUCIAL: Calculer les offsets vidéo-relatifs car la base n'a pas les gaps de la timeline
          const allSubtitles: any[] = [];
          let currentVideoOffset = 0;

          // analysis.segments ne contient que les clips vidéo (sans zones noires) dans l'ordre de concat
          for (const segment of analysis.segments) {
            const clip = segment.clip!;

            if (clip.subtitles && clip.subtitles[activeSubtitleLanguage]) {
              const clipSubtitles = clip.subtitles[activeSubtitleLanguage];
              for (const sub of clipSubtitles) {
                // Les sous-titres sont stockés avec des temps absolus sur la timeline
                // sub.start et sub.end sont déjà ajustés pour le playbackRate
                // Donc (sub.start - clip.start) donne directement la position relative 
                // dans le clip tel qu'il apparaît dans la vidéo exportée
                const relativePosInClip = sub.start - clip.start;
                const relativeEndInClip = sub.end - clip.start;

                // Filtrer les sous-titres qui sont hors du clip visible
                if (relativePosInClip >= 0 && relativePosInClip <= segment.duration) {
                  allSubtitles.push({
                    ...sub,
                    start: currentVideoOffset + Math.max(0, relativePosInClip),
                    end: currentVideoOffset + Math.min(relativeEndInClip, segment.duration)
                  });
                }
              }
            }
            currentVideoOffset += clip.duration;
          }

          const finalBlob = await this.addSubtitlesWithFFmpeg(
            baseVideoBlob,
            allSubtitles, // Passer les sous-titres déjà offsetés
            activeSubtitleLanguage,
            subtitleStyle
          );

          this.updateProgress({ phase: 'complete', progress: 100, message: 'Export terminé !' });
          return finalBlob;
        }

        // Cas 2: Overlays (text/image/intro/outro) => WebCodecs (SANS sous-titres)
        // NOTE: Les sous-titres ne sont ajoutés qu'en Cas 1 (sans overlays) en piste soft
        // Dans ce cas avec overlays, pas de sous-titres du tout (ni burn, ni soft)
        const introClip = analysis.overlayClips.find(c => c.clipRole === 'intro');
        const outroClip = analysis.overlayClips.find(c => c.clipRole === 'outro');
        const otherOverlays = analysis.overlayClips.filter(c =>
          c.clipRole !== 'intro' && c.clipRole !== 'outro'
        );

        this.updateProgress({
          phase: 'muxing',
          progress: 75,
          message: 'Composition avec overlays (rendu direct)...'
        });

        // Composer : rendu direct des clips sources + intro/outro + overlays
        // On passe null pour baseVideoBlob car on va lire les sources directement
        const finalBlob = await this.addOverlays(
          baseVideoBlob,
          introClip,
          outroClip,
          otherOverlays,
          clips,
          tracks,
          activeSubtitleLanguage,
          subtitleStyle,
          analysis.totalDuration,
          analysis.segments
        );

        this.updateProgress({ phase: 'complete', progress: 100, message: 'Export terminé !' });
        return finalBlob;
      }

      // Cas 3: Pas d'overlays ni de sous-titres => Retourner la base directement
      if (baseVideoBlob) {
        this.updateProgress({ phase: 'complete', progress: 100, message: 'Export terminé !' });
        return baseVideoBlob;
      }

      throw new Error('Etat invalide: ni base ni overlays');

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      this.updateProgress({
        phase: 'error',
        progress: 0,
        message: `Erreur: ${errorMessage}`
      });
      throw error;
    } finally {
      await this.cleanup();
    }
  }

  /**
   * Analyser la timeline : séparer clips vidéo des overlays
   */
  private analyzeTimeline(clips: Clip[], tracks: Track[]): TimelineAnalysis {
    const segments: TimelineSegment[] = [];
    const overlayClips: Clip[] = [];

    // Filtrer les clips visibles
    const visibleClips = clips.filter(clip => {
      const track = tracks.find(t => t.id === clip.trackId);
      return track?.isVisible;
    });

    // Séparer clips vidéo des overlays
    const videoClips: Clip[] = [];

    for (const clip of visibleClips) {
      // Overlays : intro/outro (avec transparence VP9), text, images, zoom, synthe, panel
      // Intro/outro sont des OVERLAYS chargés depuis leurs sources VP9 (pas générés!)
      if (clip.clipRole === 'intro' ||
        clip.clipRole === 'outro' ||
        clip.type === 'text' ||
        clip.type === 'image' ||
        clip.type === 'zoom' ||
        clip.type === 'synthe' ||
        clip.type === 'panel') {
        overlayClips.push(clip);
      }
      // Clips vidéo normaux (SANS clipRole) vont dans la base
      else if (clip.type === 'video' && getClipExportSourceUrl(clip)) {
        videoClips.push(clip);
      } else if (clip.type === 'video') {
        console.warn('[ConcatExporter] Video clip excluded from fast path (missing URL):', {
          id: clip.id,
          name: clip.name,
          hasUrl: !!getClipExportSourceUrl(clip)
        });
      }
    }

    // Calculer la durée totale de la timeline
    const totalDuration = Math.max(...visibleClips.map(c => c.start + c.duration), 0);

    // Trier les clips vidéo par ordre chronologique
    videoClips.sort((a, b) => a.start - b.start);

    // Créer les segments : UNIQUEMENT les clips vidéo, collés bout à bout
    // On ne crée PAS de zones noires - on colle juste les clips ensemble
    for (const clip of videoClips) {
      segments.push({
        type: 'video',
        clip,
        start: clip.start, // Position originale sur timeline (pour overlays)
        duration: clip.duration,
      });
    }

    console.log('[ConcatExporter] Timeline analysis:', {
      totalDuration,
      videoClips: videoClips.length,
      overlayClips: overlayClips.length,
      segments: segments.length,
    });

    return {
      segments,
      overlayClips,
      totalDuration,
    };
  }

  /**
   * Vérifier si les clips contiennent des sous-titres à exporter
   */
  private checkForSubtitles(clips: Clip[], activeSubtitleLanguage: string): boolean {
    return clips.some(clip =>
      clip.subtitles &&
      clip.subtitles[activeSubtitleLanguage] &&
      clip.subtitles[activeSubtitleLanguage].length > 0
    );
  }

  /**
   * Ajouter les sous-titres avec FFmpeg en mode MUXING (rapide - pas de ré-encodage)
   * Les sous-titres sont ajoutés comme piste séparée (soft subtitles), activable/désactivable
   */
  private async addSubtitlesWithFFmpeg(
    baseVideoBlob: Blob,
    allSubtitles: any[],
    activeSubtitleLanguage: string,
    subtitleStyle: SubtitleStyle
  ): Promise<Blob> {
    if (!this.ffmpeg) throw new Error('FFmpeg non initialisé');

    this.updateProgress({
      phase: 'muxing',
      progress: 75,
      message: 'Préparation des sous-titres...'
    });

    if (allSubtitles.length === 0) {
      // Pas de sous-titres, retourner la base
      return baseVideoBlob;
    }

    // Convertir en format SRT (déjà triés par l'appelant via l'itération des segments)
    const srtContent = this.generateSRT(allSubtitles.sort((a, b) => a.start - b.start));

    // Écrire les fichiers dans FFmpeg
    await this.ffmpeg.writeFile('base_video.mp4', new Uint8Array(await baseVideoBlob.arrayBuffer()));
    await this.ffmpeg.writeFile('subtitles.srt', new TextEncoder().encode(srtContent));

    this.updateProgress({
      phase: 'muxing',
      progress: 85,
      message: 'Muxing des sous-titres (pas de ré-encodage)...'
    });

    // Muxer les sous-titres comme piste séparée SANS réencoder la vidéo
    // -c copy = copie tous les streams sans réencodage (ultra rapide!)
    // -c:s mov_text = encoder les sous-titres en format MP4
    await this.execFFmpeg([
      '-i', 'base_video.mp4',
      '-i', 'subtitles.srt',
      '-c', 'copy',              // Copier vidéo et audio sans réencodage
      '-c:s', 'mov_text',        // Encoder les sous-titres en format MP4
      '-metadata:s:s:0', `language=${activeSubtitleLanguage}`, // Définir la langue (ISO code)
      '-metadata:s:s:0', `title=${this.getLanguageTitle(activeSubtitleLanguage)}`, // Nom lisible (ex: Français)
      '-disposition:s:0', 'default', // Définir comme piste par défaut
      'output_with_subs.mp4'
    ], 'mux subtitles track');

    this.updateProgress({
      phase: 'muxing',
      progress: 95,
      message: 'Finalisation...'
    });

    // Lire le résultat
    const outputData = await this.ffmpeg.readFile('output_with_subs.mp4');
    const outputBlob = new Blob([outputData as any], { type: 'video/mp4' });

    // Nettoyer
    await this.safeDelete('base_video.mp4');
    await this.safeDelete('subtitles.srt');
    await this.safeDelete('output_with_subs.mp4');

    console.log('[ConcatExporter] Sous-titres muxés (soft subtitles):', outputBlob.size, 'bytes');

    return outputBlob;
  }

  /**
   * Générer un fichier SRT à partir des sous-titres
   */
  private generateSRT(subtitles: any[]): string {
    return subtitles.map((sub, index) => {
      const startTime = this.formatSRTTime(sub.start);
      const endTime = this.formatSRTTime(sub.end);
      const text = sub.text;

      return `${index + 1}\n${startTime} --> ${endTime}\n${text}\n`;
    }).join('\n');
  }

  /**
   * Formater le temps en format SRT (HH:MM:SS,mmm)
   */
  private formatSRTTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    const milliseconds = Math.floor((seconds % 1) * 1000);

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')},${milliseconds.toString().padStart(3, '0')}`;
  }

  /**
   * Charger ffmpeg.wasm (utilise l'instance partagée pré-chargée)
   */
  private async loadFFmpeg(): Promise<void> {
    if (this.isFFmpegLoaded && this.ffmpeg) return;

    console.log('[ConcatExporter] Récupération de l\'instance FFmpeg partagée...');

    // Utiliser l'instance partagée qui a été pré-chargée au démarrage
    this.ffmpeg = await FFmpegService.getInstance();
    this.ffmpegLogs = [];

    // Logs
    this.ffmpeg.on('log', ({ message }) => {
      console.log('[FFmpeg]', message);
      this.recordFFmpegLog(message);
    });

    // Progress
    this.ffmpeg.on('progress', ({ progress }) => {
      console.log('[FFmpeg Progress]', Math.round(progress * 100) + '%');
    });

    this.isFFmpegLoaded = true;
    console.log('[ConcatExporter] FFmpeg prêt (instance partagée) ✓');
  }

  /**
   * Traiter un clip avec une vitesse de lecture modifiée
   * Applique les filtres setpts (video) et atempo (audio) pour changer la vitesse
   */
  private async processClipWithSpeed(
    sourceFilename: string,
    trimStart: number,
    duration: number,
    playbackRate: number,
    index: number
  ): Promise<string> {
    if (!this.ffmpeg) throw new Error('FFmpeg non initialisé');

    const outputFilename = `speed_${index}.mp4`;

    // Calculer la durée d'entrée (combien de secondes de la source on doit lire)
    // duration est la durée sur la timeline.
    // Si playbackRate = 2.0 (accéléré), on doit lire 2s de source pour faire 1s de timeline.
    const inputDuration = duration * playbackRate;

    // Préparer les filtres vidéo et audio
    // setpts change le timing des frames. atempo change la vitesse audio.
    // On ajoute aussi une normalisation du framerate (fps=30) pour éviter le slow-motion en concat
    const videoFilter = `setpts=PTS/${playbackRate},fps=${this.config.fps}`;

    // Pour l'audio, atempo est limité à 0.5-2.0, donc on doit chaîner si nécessaire
    let audioFilter = '';
    let rate = playbackRate;

    // Chaîner plusieurs atempo si le taux dépasse les limites
    while (rate > 2.0) {
      audioFilter += audioFilter ? ',atempo=2.0' : 'atempo=2.0';
      rate /= 2.0;
    }
    while (rate < 0.5) {
      audioFilter += audioFilter ? ',atempo=0.5' : 'atempo=0.5';
      rate /= 0.5;
    }
    audioFilter += audioFilter ? `,atempo=${rate}` : `atempo=${rate}`;

    console.log(`[ConcatExporter] Processing clip ${index} with speed ${playbackRate}x: inputDuration=${inputDuration}s, videoFilter="${videoFilter}", audioFilter="${audioFilter}"`);

    // Extraire et appliquer les filtres
    const ffmpegArgs = [
      '-ss', trimStart.toFixed(6),
      '-t', inputDuration.toFixed(6),
      '-i', sourceFilename,
      '-filter:v', videoFilter,
      '-filter:a', audioFilter,
      '-r', this.config.fps.toString(),
      '-c:v', 'libx264',
      '-preset', 'ultrafast',
      '-c:a', 'aac',
      '-b:a', '128k',
      outputFilename
    ];

    await this.execFFmpeg(ffmpegArgs, `process clip ${index} with speed ${playbackRate}x`);

    return outputFilename;
  }

  /**
   * ÉTAPE 1 : Créer la base vidéo (coller tous les clips vidéo bout à bout)
   */
  /**
   * ÉTAPE 1 : Créer la base vidéo (coller tous les clips vidéo bout à bout)
   * MODE INSTANTANÉ : Utilise "Virtual Concat" (manifeste avec points d'entrée/sortie)
   */
  private async createBaseVideo(analysis: TimelineAnalysis): Promise<Blob> {
    if (!this.ffmpeg) throw new Error('FFmpeg non initialisé');

    const totalSegments = analysis.segments.length;
    const manifestEntries: string[] = [];
    const usedSourceFiles = new Set<string>();
    const processedFiles: string[] = [];

    // --- DÉTECTION DU FRAMERATE CIBLE ---
    // On détecte le FPS du premier clip vidéo "normal" pour l'utiliser comme base de normalisation
    let targetFPS = this.config.fps; // 30 par défaut

    // Pour le mode instantané, on fait confiance au premier clip ou à la config
    // On ne force pas le re-processing systématique pour garder la vitesse "Instant"

    try {
      for (let i = 0; i < totalSegments; i++) {
        if (this.aborted) throw new Error('Export annulé');

        const segment = analysis.segments[i];
        const progress = 15 + Math.floor((i / totalSegments) * 40); // 15-55%

        if (['video', 'synthe', 'panel'].includes(segment.type) && segment.clip?.url) {
          const clip = segment.clip;
          const sourceFilename = await this.ensureSourceFile(clip);
          usedSourceFiles.add(sourceFilename);

          const trimStart = clip.trimStart || 0; // Position de début dans la vidéo source
          const playbackRate = clip.playbackRate ?? 1.0;

          this.updateProgress({
            phase: 'encoding',
            progress,
            message: `Préparation source ${i + 1}/${totalSegments}...`
          });

          // MODE INSTANTANÉ :
          // Si vitesse normale (1.0) -> Utiliser inpoint/outpoint (ZERO copie, ZERO encodage)
          // Si vitesse modifiée (!= 1.0) -> Ré-encodage nécessaire
          if (playbackRate === 1.0) {

            // Pas de processing, juste du référencement temporel dans le manifest
            const inpoint = Math.max(0, trimStart);
            const outpoint = inpoint + segment.duration;

            console.log(`[ConcatExporter] Clip ${i} INSTANT COPY: ${sourceFilename} [${inpoint.toFixed(3)}-${outpoint.toFixed(3)}]`);

            manifestEntries.push(
              `file '${sourceFilename}'`,
              `inpoint ${inpoint.toFixed(6)}`,
              `outpoint ${outpoint.toFixed(6)}`,
              ''
            );

          } else {
            // Vitesse modifiée : on doit ré-encoder
            this.updateProgress({
              phase: 'encoding',
              progress,
              message: `Application vitesse ${playbackRate}x au clip ${i + 1}/${totalSegments}...`
            });

            const segmentFilename = await this.processClipWithSpeed(
              sourceFilename,
              trimStart,
              segment.duration,
              playbackRate,
              i
            );
            console.log(`[ConcatExporter] Clip ${i} processed (speed): ${segmentFilename}`);

            processedFiles.push(segmentFilename);
            manifestEntries.push(
              `file '${segmentFilename}'`,
              ''
            );
          }
        }
      }

      if (manifestEntries.length === 0) {
        throw new Error('Impossible de créer la vidéo de base: aucun segment valide');
      }

      this.updateProgress({
        phase: 'muxing',
        progress: 55,
        message: 'Création de la playlist concat...'
      });

      // Créer le fichier concat.txt
      await this.ffmpeg.writeFile(
        'concat.txt',
        new TextEncoder().encode(manifestEntries.join('\n'))
      );

      console.log('[ConcatExporter] Concaténation via manifest de', totalSegments, 'segments...');

      this.updateProgress({
        phase: 'muxing',
        progress: 60,
        message: 'Assemblage final (Concat)...'
      });

      const concatArgsBase = [
        '-fflags', '+genpts', // Régénérer les timestamps pour éviter les décalages
        '-f', 'concat',
        '-safe', '0',
        '-i', 'concat.txt',
        '-c', 'copy',
        '-avoid_negative_ts', 'make_zero',
      ];

      try {
        await this.execFFmpeg([
          ...concatArgsBase,
          '-bsf:a', 'aac_adtstoasc',
          'base.mp4'
        ], 'concat base video (aac filter)');
      } catch (error) {
        if (error instanceof FFmpegOutOfMemoryError) {
          throw error;
        }
        console.warn('[ConcatExporter] Concat fail, retry without filter...', error);
        await this.execFFmpeg([
          ...concatArgsBase,
          'base.mp4'
        ], 'concat base video');
      }

      this.updateProgress({
        phase: 'muxing',
        progress: 70,
        message: 'Lecture du fichier base...'
      });

      const outputData = await this.ffmpeg.readFile('base.mp4');
      const outputBlob = new Blob([outputData as any], { type: 'video/mp4' });

      console.log('[ConcatExporter] Base vidéo créée:', outputBlob.size, 'bytes');

      return outputBlob;

    } finally {
      await this.safeDelete('concat.txt');
      await this.safeDelete('base.mp4');

      // Nettoyer tous les segments temporaires
      for (const file of processedFiles) {
        await this.safeDelete(file);
      }

      await this.purgeSources(usedSourceFiles);
    }
  }



  /**
   * ÉTAPE 2 : Ajouter overlays par-dessus la vidéo de base
   *
   * Charge la base vidéo + intro/outro sources et superpose tout frame par frame avec WebCodecs
   */
  private async addOverlays(
    baseVideoBlob: Blob,
    introClip: Clip | undefined,
    outroClip: Clip | undefined,
    otherOverlays: Clip[],
    allClips: Clip[],
    tracks: Track[],
    activeSubtitleLanguage: string,
    subtitleStyle: SubtitleStyle,
    totalDuration: number,
    videoSegments: TimelineSegment[]
  ): Promise<Blob> {
    this.updateProgress({
      phase: 'muxing',
      progress: 75,
      message: 'Chargement de la base vidéo...'
    });

    // NOTE: On ne crée plus de baseVideo unique pour les exports FX
    // On va charger les clips sources à la demande ("virtual concat")
    let currentSourceVideo: HTMLVideoElement | null = null;
    let currentSourceUrl: string | null = null;

    // Pré-charger le premier clip si possible
    if (videoSegments.length > 0 && videoSegments[0].clip && getClipExportSourceUrl(videoSegments[0].clip)) {
      this.updateProgress({
        phase: 'muxing',
        progress: 75,
        message: 'Chargement des sources vidéo...'
      });
    }

    // Charger intro si présente (directement depuis la source VP9 avec alpha)
    let introVideo: HTMLVideoElement | null = null;
    const introUrl = introClip ? getClipExportSourceUrl(introClip) : undefined;
    if (introClip && introUrl) {
      this.updateProgress({
        phase: 'muxing',
        progress: 76,
        message: 'Chargement intro source...'
      });

      introVideo = window.document.createElement('video');
      introVideo.crossOrigin = 'anonymous';
      introVideo.muted = true;
      introVideo.preload = 'metadata';

      await new Promise<void>((resolve, reject) => {
        introVideo!.addEventListener('loadedmetadata', () => resolve(), { once: true });
        introVideo!.addEventListener('error', (e) => reject(new Error('Erreur chargement intro source')), { once: true });
        introVideo!.src = introUrl;
        introVideo!.load();
      });

      console.log('[ConcatExporter] Intro source chargée:', introVideo.duration, 's, alpha VP9 préservé');
    }

    // Charger outro si présente (directement depuis la source VP9 avec alpha)
    let outroVideo: HTMLVideoElement | null = null;
    const outroUrl = outroClip ? getClipExportSourceUrl(outroClip) : undefined;
    if (outroClip && outroUrl) {
      this.updateProgress({
        phase: 'muxing',
        progress: 77,
        message: 'Chargement outro source...'
      });

      outroVideo = window.document.createElement('video');
      outroVideo.crossOrigin = 'anonymous';
      outroVideo.muted = true;
      outroVideo.preload = 'metadata';

      await new Promise<void>((resolve, reject) => {
        outroVideo!.addEventListener('loadedmetadata', () => resolve(), { once: true });
        outroVideo!.addEventListener('error', (e) => reject(new Error('Erreur chargement outro source')), { once: true });
        outroVideo!.src = outroUrl;
        outroVideo!.load();
      });

      console.log('[ConcatExporter] Outro source chargée:', outroVideo.duration, 's, alpha VP9 préservé');
    }

    this.updateProgress({
      phase: 'muxing',
      progress: 78,
      message: 'Initialisation des encodeurs...'
    });

    // Initialiser les composants d'encodage
    const resolution = ASPECT_RATIOS[this.config.aspectRatio];
    const canvas = new OffscreenCanvas(resolution.width, resolution.height);
    const ctx = canvas.getContext('2d', { alpha: true, desynchronized: true });

    if (!ctx) {
      throw new Error('Impossible de créer le contexte canvas');
    }

    // Initialiser WebM writer (SANS audio, on l'ajoutera avec ffmpeg après)
    const webmWriter = new WebMWriter({
      width: resolution.width,
      height: resolution.height,
      fps: this.config.fps,
      hasAudio: false, // On multiplexera l'audio avec ffmpeg après
      audioSampleRate: this.config.audioSampleRate,
    });

    // Initialiser VideoEncoder
    const videoEncoder = new VideoEncoder({
      output: (chunk, metadata) => {
        webmWriter.addVideoChunk(chunk, metadata);
      },
      error: (error) => {
        console.error('Video encoder error:', error);
        throw error;
      }
    });

    videoEncoder.configure({
      codec: 'vp09.00.10.08',
      width: resolution.width,
      height: resolution.height,
      bitrate: this.config.videoBitrate,
      framerate: this.config.fps,
      latencyMode: 'quality',
    });

    // Initialiser FrameRenderer pour les autres overlays (text/images/zoom)
    const frameRenderer = new FrameRenderer(resolution.width, resolution.height);
    await frameRenderer.preloadMedia(otherOverlays);

    this.updateProgress({
      phase: 'encoding',
      progress: 80,
      message: 'Rendu des overlays...'
    });

    // Calculer le nombre total de frames
    const totalFrames = Math.ceil(totalDuration * this.config.fps);
    const frameDuration = 1000000 / this.config.fps; // en microsecondes

    // Rendre chaque frame
    for (let frame = 0; frame < totalFrames; frame++) {
      if (this.aborted) {
        throw new Error('Export annulé');
      }

      const currentTime = frame / this.config.fps;

      // Trouver le segment actif pour ce frame
      const activeSegment = videoSegments.find(s =>
        currentTime >= s.start && currentTime < s.start + s.duration
      );

      // Clear canvas
      ctx.clearRect(0, 0, resolution.width, resolution.height);

      // 1. Dessiner le clip source si on est dans un segment vidéo
      const activeSourceUrl = activeSegment?.clip ? getClipExportSourceUrl(activeSegment.clip) : undefined;
      if (activeSegment && activeSegment.clip && activeSourceUrl) {
        const clip = activeSegment.clip;

        // Charger le clip si nécessaire (changement de source)
        if (currentSourceUrl !== activeSourceUrl) {
          if (currentSourceVideo) {
            currentSourceVideo.pause();
            currentSourceVideo.removeAttribute('src'); // clean up
            currentSourceVideo.load();
          }

          currentSourceVideo = window.document.createElement('video');
          currentSourceVideo.crossOrigin = 'anonymous';
          currentSourceVideo.muted = true;
          // FIX: Use metadata preload to avoid crashing browser with large files (6GB+)
          // Seeking might be slightly slower but it's safe.
          currentSourceVideo.preload = 'metadata';
          currentSourceVideo.src = activeSourceUrl;
          currentSourceUrl = activeSourceUrl;

          await new Promise<void>((resolve, reject) => {
            currentSourceVideo!.addEventListener('loadedmetadata', () => resolve(), { once: true });
            currentSourceVideo!.addEventListener('error', () => reject(new Error(`Erreur chargement clip ${clip.name}`)), { once: true });
            currentSourceVideo!.load();
          });
        }

        if (currentSourceVideo) {
          // Calculer le temps exact dans le clip source
          // (currentTime - segment.start) = temps depuis le début du segment sur la timeline
          // * playbackRate = temps à parcourir dans le fichier source
          // + trimStart = offset de départ dans le fichier source
          const timeInSegment = currentTime - activeSegment.start;
          const playbackRate = clip.playbackRate ?? 1.0;
          const seekTime = (timeInSegment * playbackRate) + (clip.trimStart || 0);

          if (Math.abs(currentSourceVideo.currentTime - seekTime) > 0.05) { // Seuil précis
            currentSourceVideo.currentTime = seekTime;
            await new Promise<void>((resolve) => {
              const checkReady = () => {
                if (currentSourceVideo!.readyState >= 2) resolve();
                else currentSourceVideo!.addEventListener('seeked', () => resolve(), { once: true });
              };
              checkReady();
            });
          }

          // Dessiner la frame
          ctx.drawImage(currentSourceVideo, 0, 0, resolution.width, resolution.height);
        }
      }

      // 2. Dessiner intro si on est dans son timing (avec transparence VP9)
      if (introVideo && introClip && currentTime >= introClip.start && currentTime < introClip.start + introClip.duration) {
        const introTime = currentTime - introClip.start;
        if (Math.abs(introVideo.currentTime - introTime) > 0.1) {
          introVideo.currentTime = introTime;
          await new Promise<void>((resolve) => {
            const checkReady = () => {
              if (introVideo!.readyState >= 2) {
                resolve();
              } else {
                introVideo!.addEventListener('seeked', () => resolve(), { once: true });
              }
            };
            checkReady();
          });
        }
        // Dessiner intro par-dessus base (alpha préservé!)
        ctx.drawImage(introVideo, 0, 0, resolution.width, resolution.height);
      }

      // 3. Dessiner outro si on est dans son timing (avec transparence VP9)
      if (outroVideo && outroClip && currentTime >= outroClip.start && currentTime < outroClip.start + outroClip.duration) {
        const outroTime = currentTime - outroClip.start;
        if (Math.abs(outroVideo.currentTime - outroTime) > 0.1) {
          outroVideo.currentTime = outroTime;
          await new Promise<void>((resolve) => {
            const checkReady = () => {
              if (outroVideo!.readyState >= 2) {
                resolve();
              } else {
                outroVideo!.addEventListener('seeked', () => resolve(), { once: true });
              }
            };
            checkReady();
          });
        }
        // Dessiner outro par-dessus base (alpha préservé!)
        ctx.drawImage(outroVideo, 0, 0, resolution.width, resolution.height);
      }

      // 4. Dessiner les autres overlays (text/images/zoom/synthe/panel)
      // NOTE: Ne pas passer activeSubtitleLanguage pour éviter de burn les sous-titres
      // Dans le cas avec overlays, les sous-titres ne sont PAS ajoutés (ni burn, ni soft)
      // CRITICAL FIX: clearCanvas=false to NOT erase the base video we just drew above!
      await frameRenderer.renderFrame(
        ctx,
        currentTime,
        otherOverlays,
        tracks,
        '', // Pas de langue de sous-titres => pas de burn
        subtitleStyle,
        'fill',  // scaleMode
        false    // clearCanvas - IMPORTANT: do NOT clear canvas, we already drew the base video
      );

      // 5. Encoder la frame
      const videoFrame = new VideoFrame(canvas, {
        timestamp: frame * frameDuration,
        duration: frameDuration,
      });

      videoEncoder.encode(videoFrame, { keyFrame: frame % (this.config.fps * 2) === 0 });
      videoFrame.close();

      // Update progress every 30 frames
      if (frame % 30 === 0) {
        const progress = 80 + Math.floor((frame / totalFrames) * 15); // 80-95%
        this.updateProgress({
          phase: 'encoding',
          progress,
          message: `Rendu overlay frame ${frame}/${totalFrames}`,
          currentFrame: frame,
          totalFrames
        });
      }
    }

    // Flush video encoder
    await videoEncoder.flush();

    this.updateProgress({
      phase: 'muxing',
      progress: 93,
      message: 'Finalisation vidéo avec overlays...'
    });

    // Récupérer la vidéo avec overlays (SANS audio)
    const videoOnlyBlob = await webmWriter.complete();

    console.log('[ConcatExporter] Vidéo avec overlays créée:', videoOnlyBlob.size, 'bytes');

    // Cleanup vidéo
    videoEncoder.close();
    frameRenderer.cleanup();
    // Note: pas besoin de révoquer intro/outro URLs car ce sont des sources directes (pas de blob)

    // ÉTAPE 3 : Multiplexer l'audio de la base avec la vidéo finale
    this.updateProgress({
      phase: 'muxing',
      progress: 95,
      message: 'Ajout de l\'audio de la base...'
    });

    if (!this.ffmpeg) throw new Error('FFmpeg non initialisé');

    console.log('[ConcatExporter] Multiplexage audio - base:', baseVideoBlob.size, 'bytes, video:', videoOnlyBlob.size, 'bytes');

    // Écrire la vidéo (sans audio) et la base (avec audio) dans ffmpeg
    await this.ffmpeg.writeFile('video_only.webm', await fetchFile(videoOnlyBlob));
    await this.ffmpeg.writeFile('base_with_audio.mp4', await fetchFile(baseVideoBlob));

    // Extraire l'audio de la base et multiplexer avec la vidéo finale
    await this.execFFmpeg([
      '-i', 'video_only.webm',     // Vidéo avec overlays (sans audio)
      '-i', 'base_with_audio.mp4', // Base (avec audio)
      '-c:v', 'copy',              // Copier la vidéo sans réencodage
      '-c:a', 'copy',              // Copier l'audio sans réencodage
      '-map', '0:v:0',             // Vidéo depuis video_only.webm
      '-map', '1:a:0',             // Audio depuis base_with_audio.mp4
      '-shortest',                 // Arrêter quand la plus courte stream finit
      'final.webm'
    ], 'multiplexage audio final');

    // Lire le résultat
    const finalData = await this.ffmpeg.readFile('final.webm');
    const finalBlob = new Blob([finalData as any], { type: 'video/webm' });

    console.log('[ConcatExporter] Vidéo finale créée:', finalBlob.size, 'bytes');

    // Nettoyer les fichiers temporaires
    await this.ffmpeg.deleteFile('video_only.webm');
    await this.ffmpeg.deleteFile('base_with_audio.mp4');
    await this.ffmpeg.deleteFile('final.webm');

    // NOTE: Pas de sous-titres en soft dans le cas avec overlays
    // Le format de sortie est WebM qui ne supporte pas bien les pistes de sous-titres séparées
    // Les sous-titres en soft (ultra rapide) sont uniquement pour le Cas 1 (sans overlays)

    return finalBlob;
  }

  private async ensureSourceFile(clipOrUrl: Clip | string): Promise<string> {
    if (!this.ffmpeg) {
      throw new Error('FFmpeg non initialisé');
    }

    const url = typeof clipOrUrl === 'string' ? clipOrUrl : getClipExportSourceUrl(clipOrUrl);
    if (!url) {
      throw new Error('Source vidéo introuvable pour le concat');
    }

    // Check cache first
    const cached = this.sourceCache.get(url);
    if (cached) {
      return cached;
    }

    // OPTIMIZATION: Use WORKERFS for large files available locally
    if (typeof clipOrUrl !== 'string') {
      const fileRef = (clipOrUrl as any)._file as File | undefined;
      if (fileRef) {
        const uniqueId = Date.now().toString() + Math.random().toString().slice(2, 6);
        const mountDir = `/mnt_concat_${uniqueId}`;
        try { await this.ffmpeg.createDir(mountDir); } catch (e) { }

        // Sanitize filename
        const ext = fileRef.name.split('.').pop() || 'mp4';
        const safeName = `source_input.${ext}`;

        // We need a File object with the safe name for WORKERFS
        const safeFile = new File([fileRef], safeName, { type: fileRef.type });

        await this.ffmpeg.mount('WORKERFS' as any, { files: [safeFile] }, mountDir);

        const sourceFilename = `${mountDir}/${safeName}`;
        console.log(`[ConcatExporter] Mounted large file at ${sourceFilename}`);

        this.sourceCache.set(url, sourceFilename);
        return sourceFilename;
      }
    }

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Impossible de télécharger la source vidéo (${response.status})`);
    }

    const sourceBlob = await response.blob();
    const sourceFilename = `source_${this.sourceSequence++}.mp4`;
    await this.ffmpeg.writeFile(sourceFilename, await fetchFile(sourceBlob));

    this.sourceCache.set(url, sourceFilename);
    return sourceFilename;
  }

  private async execFFmpeg(args: string[], context: string): Promise<void> {
    if (!this.ffmpeg) {
      throw new Error('FFmpeg non initialisé');
    }

    this.resetFFmpegLogBuffer();

    try {
      await this.ffmpeg.exec(args);
    } catch (error) {
      if (isLikelyFFmpegOutOfMemoryError(error, this.ffmpegLogs)) {
        this.updateProgress({
          phase: 'preparing',
          progress: 0,
          message: 'Mémoire FFmpeg insuffisante. Tentative d\'une stratégie alternative...'
        });

        throw new FFmpegOutOfMemoryError(
          `FFmpeg est à court de mémoire pendant ${context}`,
          {
            cause: error,
            logs: [...this.ffmpegLogs],
          }
        );
      }

      throw error instanceof Error ? error : new Error(String(error));
    }
  }

  private async safeDelete(filename: string): Promise<void> {
    if (!this.ffmpeg || !filename) {
      return;
    }

    try {
      await this.ffmpeg.deleteFile(filename);
    } catch (e) {
      // Ignore delete errors
    }
  }

  private async purgeSources(files?: Set<string>): Promise<void> {
    if (!this.ffmpeg) {
      return;
    }

    const targets = files ?? new Set(this.sourceCache.values());

    for (const filename of targets) {
      await this.safeDelete(filename);
    }

    if (files) {
      for (const [url, filename] of Array.from(this.sourceCache.entries())) {
        if (files.has(filename)) {
          this.sourceCache.delete(url);
        }
      }
    } else {
      this.sourceCache.clear();
    }

    if (this.sourceCache.size === 0) {
      this.sourceSequence = 0;
    }
  }

  private resetFFmpegLogBuffer(): void {
    this.ffmpegLogs = [];
  }

  private recordFFmpegLog(message: string): void {
    this.ffmpegLogs.push(message);
    if (this.ffmpegLogs.length > 200) {
      this.ffmpegLogs.shift();
    }
  }

  /**
   * Nettoyer les ressources
   */
  private async cleanup(): Promise<void> {
    if (this.ffmpeg && this.isFFmpegLoaded) {
      await this.purgeSources();

      // Terminer ffmpeg
      await this.ffmpeg.terminate();
      FFmpegService.markTerminated(); // Notifier le service que l'instance est terminée
      this.ffmpeg = null;
      this.isFFmpegLoaded = false;
      this.resetFFmpegLogBuffer();
    }
  }

  /**
   * Update progress callback
   */
  private updateProgress(progress: ExportProgress): void {
    this.progressCallback(progress);
  }
  /**
   * Obtener le nom lisible de la langue pour les métadonnées
   */
  private getLanguageTitle(langCode: string): string {
    const titles: Record<string, string> = {
      'fr': 'Français',
      'en': 'English',
      'es': 'Español',
      'pt': 'Português',
    };
    return titles[langCode] || langCode.toUpperCase();
  }
}
